/**
 * Bank statements and the transactions on them.
 *
 * Laid out for the person working through a statement: where the account
 * stands at the top (what is left to post, what it lacks, what is held), the
 * accounts as cards to switch between, then the lines themselves with the
 * category and department set right on each one. Everything that is not daily
 * work -- importing a statement, the categories, an opening balance, a new
 * bank account -- opens in a panel from the toolbar rather than sitting open
 * on the page.
 *
 * Import is preview-then-confirm. Importing is not reversible in any useful
 * sense — the rows become the bank's version of events — so the counts, the
 * duplicates and any refusal are shown before anything is written.
 */

import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  api,
  formatInr,
  type Account,
  type BankCategory,
  type Department,
  type Location,
} from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Field, Loading, Note, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import AddBankAccount, { KINDS_AT_A_BRANCH } from './AddBankAccount'
import EditBankAccount from './EditBankAccount'
import AddCategory from './AddCategory'
import OpeningBalance from './OpeningBalance'
import GstBillPanel, { gstBillNote, type GstBill } from './GstBill'
import PayBills, { vendorPaymentNote, type OpenBillsHint, type VendorPayment } from './PayBills'
import SearchSelect from '../components/SearchSelect'

/**
 * Rows per page. The list used to be capped: it was 200 against a real
 * statement of 250, and the fifty that fell off did so in silence. A pager
 * says how many there are in all and walks through every one of them, and
 * "select all" means exactly the page on screen.
 */
const PAGE = 20

interface TxnPage {
  rows: Txn[]
  total: number
  offset: number
  limit: number
}

interface Summary {
  total: number
  in_books: number
  to_post: number
  to_post_in_paise: number
  to_post_out_paise: number
  missing_category: number
  missing_department: number
  held: number
  first_date: string | null
  last_date: string | null
}

interface BankAccountRow {
  id: string
  name: string
  opening_balance_paise: number | null
  opening_balance_date: string | null
  bank_name: string
  account_number_masked: string
  /** The branch. Missing on an account added before the form asked for it. */
  ifsc: string | null
  kind: string
  /** The ledger account this one mirrors. One to one, which is why the add
   *  form needs it: an account already spoken for is not offered again. */
  gl_account_id: string
  gl_account_code: string | null
  /** draft | submitted | returned | approved. Nothing may be loaded against
   *  an account until a Finance Head has agreed to it. */
  status: string
  approved_at: string | null
  is_active: boolean
}

interface PreviewLine {
  row_index: number
  txn_date: string
  value_date: string | null
  narration: string
  reference: string | null
  direction: 'debit' | 'credit'
  amount_paise: number
  balance_after_paise: number | null
  is_new: boolean
  posting_state: string | null
}

interface Preview {
  filename: string
  bank: string | null
  parser_id: string
  row_count: number
  new_count: number
  duplicate_count: number
  opening_balance_paise: number | null
  closing_balance_paise: number | null
  period_start: string | null
  period_end: string | null
  warnings: string[]
  refusals: string[]
  importable: boolean
  lines: PreviewLine[]
}

interface Txn {
  id: string
  bank_account_id: string
  txn_date: string
  amount_paise: number
  //: The bank's balance after this line, as the statement printed it.
  balance_after_paise: number | null
  direction: string
  narration_raw: string
  counterparty: string | null
  reference: string | null
  channel: string
  category_id: string | null
  //: Which clinic banked this. Carried when a rule or a question knows it,
  //: and what the clinic-wise deposit report reads -- not chosen here.
  location_id: string | null
  //: Whose spending it was. The second thing a line has to say before it
  //: reaches the ledger, beside what it was for.
  department_id: string | null
  posting_state: string
  match_state: string
  needs_review: boolean
  transfer_pair_id: string | null
  journal_entry_id: string | null
  control_state: string
  claimed_journal_number: string | null
  //: An open question holds the line out of the ledger until somebody
  //: answers, or until it is posted anyway with a reason.
  open_question_id: string | null
  open_question_with: string | null
  unplanned_outstanding: string | null
  unplanned_accepted_by: string | null
  posting_blocked: string | null
  //: Whose standing approval this UPI debit goes (or went) through on.
  standing_approval_by: string | null
  //: Whose money it was, where the category's account keeps a balance per
  //: person (Loans from directors) and so needs it said before posting.
  party_id: string | null
  party_name: string | null
  party_required: boolean
  //: "director" when the line posts to 2530 and so names a director ledger;
  //: "investment" when it posts to 1530 or 4950 and names an investment ledger.
  party_kind_expected: string | null
  //: An answered payment question on the line, and whether the line already
  //: says what the answer said.
  answer: {
    question_id: string
    answered_by: string | null
    text: string | null
    category_id: string | null
    location_id: string | null
    department_id: string | null
    on_line: boolean
  } | null
  category_locked: boolean
  //: Clinic invoices the money-in engine mapped to this credit.
  invoices_mapped: number
  //: The GST bill recorded from this payment, when there is one.
  gst_bill: GstBill | null
  //: The payment that sets this debit against a vendor's bills.
  vendor_payment: VendorPayment | null
  //: The debit looks like it paid a vendor who has bills open.
  open_bills: OpenBillsHint | null
}

interface AskOptions {
  finance_heads: { id: string; name: string }[]
}

type PanelKind = 'import' | 'categories' | 'opening' | 'add-account' | null

//: A line already in the books: posted from here, or claimed by a document
//: (a settled payment request) that posted it. Neither is re-categorised or
//: posted again. A claimed line whose journal has gone is not in the books,
//: which is why this reads the journal number rather than the state alone.
function inTheBooks(t: Txn): boolean {
  return (
    t.posting_state === 'posted' ||
    t.posting_state === 'excluded' ||
    (t.posting_state === 'claimed' && t.claimed_journal_number !== null)
  )
}

//: Both dimensions, or it does not post. Said here as well as by the server
//: so the button explains itself rather than refusing after the click -- the
//: refusal itself lives in `posting.py`, which is what actually enforces it.
//: A payment its GST bill can be recorded against: money out that nothing
//: else speaks for -- not yet in the books, or posted on its own. One the
//: bank debit control holds is offered too: its bill goes to the Finance
//: Head, whose approval approves the payment. The server checks it all again.
function mayCarryAGstBill(t: Txn): boolean {
  return (
    t.direction === 'debit' &&
    t.gst_bill === null &&
    t.transfer_pair_id === null &&
    t.open_question_id === null &&
    (!inTheBooks(t) || (t.posting_state === 'posted' && t.claimed_journal_number === null))
  )
}

//: A payment that can be set against a vendor's bills: money out that no
//: other document speaks for -- not yet in the books, or posted on its own
//: (which moves it, with the Finance Head's approval). One already paying
//: bills opens to show them. The server checks it all again.
function mayPayBills(t: Txn): boolean {
  if (t.vendor_payment) return true
  return (
    t.direction === 'debit' &&
    t.gst_bill === null &&
    t.transfer_pair_id === null &&
    t.open_question_id === null &&
    t.posting_state !== 'excluded' &&
    (!inTheBooks(t) || (t.posting_state === 'posted' && t.claimed_journal_number === null))
  )
}

function classified(t: Txn): boolean {
  return t.category_id !== null && t.department_id !== null
}

function missingOn(t: Txn): string {
  if (t.category_id === null && t.department_id === null) return 'a category and a department'
  return t.category_id === null ? 'a category' : 'a department'
}

const CONTROL_LABELS: Record<string, string> = {
  awaiting_request: 'awaiting request',
  unapproved: 'no approval',
  mismatch: 'amount mismatch',
  duplicate_suspect: 'paid twice?',
}

const STATUS_TONE: Record<string, string> = {
  approved: 'pass',
  submitted: 'advisory',
  returned: 'blocking',
  draft: 'closed',
}

//: The one badge that says where a line stands, and one line of why.

//: Lines on an account kept per person or per holding name a ledger from that
//: register (Books), not a vendor: 2530 a director, 1530/4950 an investment.
const SUBLEDGER: Record<
  string,
  { label: string; detail: string; none: string; screen: string; path: string }
> = {
  director: {
    label: 'Director ledger',
    detail: 'choose the director under the category',
    none: "No director ledger exists for this line. Create or activate the director's ledger before posting.",
    screen: 'Director ledgers',
    path: '/books/director-ledgers',
  },
  investment: {
    label: 'Investment ledger',
    detail: 'choose the investment under the category',
    none: 'No investment ledger is open for this line. Add the investment, or reopen its ledger, before posting.',
    screen: 'Investment ledgers',
    path: '/books/investment-ledgers',
  },
}

function standing(t: Txn): {
  label: string
  tone: string
  detail: string | null
} {
  //: Said wherever the line stands, so whose approval it relies on is never hidden.
  const onStanding = t.standing_approval_by ? `UPI · approved by ${t.standing_approval_by}` : null
  if (inTheBooks(t))
    return {
      label: 'In the books',
      tone: 'pass',
      detail: t.claimed_journal_number ? `via ${t.claimed_journal_number}` : onStanding,
    }
  if (t.transfer_pair_id !== null)
    return { label: 'Transfer', tone: 'closed', detail: 'half of a transfer' }
  if (t.open_question_id !== null)
    return {
      label: 'Asked',
      tone: 'advisory',
      detail: `with ${t.open_question_with ?? 'someone'}`,
    }
  if (t.unplanned_accepted_by && !classified(t))
    return {
      label: `Needs ${missingOn(t).replace(/^a /, '').replace(' and a ', ' & ')}`,
      tone: 'advisory',
      detail: `accepted by ${t.unplanned_accepted_by}`,
    }
  if (t.party_required && !t.party_id && !t.unplanned_outstanding)
    return t.party_kind_expected && SUBLEDGER[t.party_kind_expected]
      ? {
          label: `Needs ${SUBLEDGER[t.party_kind_expected].label.toLowerCase()}`,
          tone: 'advisory',
          detail: SUBLEDGER[t.party_kind_expected].detail,
        }
      : {
          label: 'Needs a name',
          tone: 'advisory',
          detail: 'whose money: choose them under the category',
        }
  if (t.answer && !t.answer.on_line && !t.unplanned_outstanding && !t.posting_blocked)
    return {
      label: 'Answered',
      tone: 'turn',
      detail: `by ${t.answer.answered_by ?? 'the person asked'} — use the answer`,
    }
  if (t.unplanned_outstanding || t.posting_blocked)
    return {
      label: 'Held',
      tone: 'blocking',
      detail: t.unplanned_outstanding ?? t.posting_blocked,
    }
  if (!classified(t))
    return {
      label: `Needs ${missingOn(t).replace(/^a /, '').replace(' and a ', ' & ')}`,
      tone: 'advisory',
      detail: onStanding,
    }
  return { label: 'Ready to post', tone: 'turn', detail: onStanding }
}

//: How far a line that is already in has got, said the way the list says it.
const ALREADY_LABELS: Record<string, string> = {
  imported: 'not categorised',
  categorized: 'categorised',
  posted: 'posted',
  claimed: 'posted',
  excluded: 'excluded',
}

type PreviewFilter = 'all' | 'new' | 'already'

/**
 * Every line of the file, so a person can see which ones are new and check
 * them against the bank's own statement before agreeing. The counts alone
 * ("1 new, 74 already imported") never said which line was the new one.
 */
//: The statement's search and filters. They run on the server: the list is
//: paged, so a filter in the browser would only ever search the page on screen.
interface LineFilters {
  q: string
  direction: '' | 'credit' | 'debit'
  channel: string
  //: '' is every category, 'none' the lines with no category yet.
  category: string
  inBooks: '' | 'yes' | 'no'
  from: string
  to: string
  //: In rupees, as typed.
  min: string
  max: string
}

const NO_FILTERS: LineFilters = {
  q: '',
  direction: '',
  channel: '',
  category: '',
  inBooks: '',
  from: '',
  to: '',
  min: '',
  max: '',
}

const CHANNELS: [string, string][] = [
  ['upi', 'UPI'],
  ['neft', 'NEFT'],
  ['rtgs', 'RTGS'],
  ['imps', 'IMPS'],
  ['pos', 'Card (POS)'],
  ['atm', 'ATM'],
  ['ach', 'Auto-debit (ACH)'],
  ['cheque', 'Cheque'],
  ['cash', 'Cash'],
  ['interest', 'Interest'],
  ['charges', 'Bank charges'],
  ['other', 'Other'],
]

const rupeesToPaise = (text: string): number | null => {
  const clean = text.replace(/[^0-9.]/g, '')
  if (!clean) return null
  const value = Number(clean)
  return Number.isFinite(value) ? Math.round(value * 100) : null
}

//: The filters as query-string parameters, each with a leading '&'.
function filterQuery(f: LineFilters): string {
  const params = new URLSearchParams()
  if (f.q.trim()) params.set('q', f.q.trim())
  if (f.direction) params.set('direction', f.direction)
  if (f.channel) params.set('channel', f.channel)
  if (f.category) params.set('category', f.category)
  if (f.inBooks) params.set('in_books', f.inBooks === 'yes' ? 'true' : 'false')
  if (f.from) params.set('date_from', f.from)
  if (f.to) params.set('date_to', f.to)
  const min = rupeesToPaise(f.min)
  if (min !== null) params.set('amount_min_paise', String(min))
  const max = rupeesToPaise(f.max)
  if (max !== null) params.set('amount_max_paise', String(max))
  const text = params.toString()
  return text ? `&${text}` : ''
}

//: How many of the dates and amounts are set, for the "More filters" button.
const moreSet = (f: LineFilters): number =>
  [f.from, f.to, f.min, f.max].filter((v) => v.trim() !== '').length

interface ClosingBalance {
  bank_account_id: string
  name: string
  kind: string
  as_at: string
  bank_paise: number | null
  books_paise: number
  difference_paise: number | null
  to_post_count: number
  to_post_paise: number
  left_out_count: number
  left_out_paise: number
  unexplained_paise: number | null
}

/** A signed amount with its sign spelt out: an overdraft is a minus. */
function signedInr(paise: number): string {
  return paise < 0 ? `−${formatInr(-paise)}` : formatInr(paise)
}

/**
 * The balance each account ends on, as the bank's last statement gives it and
 * as the books have it on the same day, and the gap taken apart: the lines not
 * in the books yet, then whatever those do not explain.
 */
function ClosingBalances({ accountId }: { accountId: string }) {
  const balances = useQuery({
    queryKey: ['bank-balances', accountId],
    queryFn: () =>
      api.get<ClosingBalance[]>(`/banking/balances${accountId ? `?account_id=${accountId}` : ''}`),
  })
  //: A clinic till has no statement; listed only when it is the one picked.
  const rows = (balances.data ?? []).filter((b) => accountId !== '' || b.bank_paise !== null)
  if (balances.error) return <ErrorNote error={balances.error} />
  if (rows.length === 0) return null
  return (
    <section className="card wide">
      <header>
        <h2>Closing balance</h2>
        <span className="hint">bank’s last statement beside the books on the same day</span>
      </header>
      <table>
        <thead>
          <tr>
            <th>Account</th>
            <th>As at</th>
            <th className="num">As per bank</th>
            <th className="num">As per books</th>
            <th className="num">Difference</th>
            <th className="num">Not yet in the books</th>
            <th className="num">Left over</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.bank_account_id}>
              <td>{b.name}</td>
              <td className="date">{b.as_at}</td>
              <td className="num">
                {b.bank_paise === null ? (
                  <span className="faint">no statement</span>
                ) : (
                  <strong>{signedInr(b.bank_paise)}</strong>
                )}
              </td>
              <td className="num">{signedInr(b.books_paise)}</td>
              <td className="num">
                {b.difference_paise === null ? '—' : signedInr(b.difference_paise)}
              </td>
              <td className="num">
                {b.to_post_count + b.left_out_count === 0 ? (
                  '—'
                ) : (
                  <>
                    {signedInr(b.to_post_paise + b.left_out_paise)}
                    <div className="faint">
                      {b.to_post_count > 0 && `${b.to_post_count} to post`}
                      {b.to_post_count > 0 && b.left_out_count > 0 && ' · '}
                      {b.left_out_count > 0 && `${b.left_out_count} left out`}
                    </div>
                  </>
                )}
              </td>
              <td className="num">
                {b.unexplained_paise === null ? (
                  '—'
                ) : b.unexplained_paise === 0 ? (
                  <span className="pill ok">agrees</span>
                ) : (
                  <span className="pill advisory">{signedInr(b.unexplained_paise)}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p
        className="faint"
        style={{ fontSize: 13, margin: 'var(--space-2) var(--space-4) var(--space-3)' }}
      >
        Lines not yet posted, or left out on purpose, explain most of any difference. What is left
        over is a line the bank and the books date or amount differently — or money the bank has
        that no imported statement shows.
      </p>
    </section>
  )
}

function PreviewLines({ lines }: { lines: PreviewLine[] }) {
  const newCount = lines.filter((l) => l.is_new).length
  //: Opens on the new lines when there are some: those are what the button
  //: will write. A file with none opens on everything.
  const [filter, setFilter] = useState<PreviewFilter>(newCount > 0 ? 'new' : 'all')
  const shown = lines.filter((l) =>
    filter === 'all' ? true : filter === 'new' ? l.is_new : !l.is_new,
  )
  const paged = usePaged(shown, filter)
  if (lines.length === 0) return null
  const tabs: [PreviewFilter, string, number][] = [
    ['all', 'All lines', lines.length],
    ['new', 'New', newCount],
    ['already', 'Already in', lines.length - newCount],
  ]
  return (
    <section className="preview-lines">
      <div className="preview-lines-head">
        <div className="seg" role="tablist" aria-label="Which lines">
          {tabs.map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={filter === key}
              className={filter === key ? 'on' : undefined}
              onClick={() => setFilter(key)}
            >
              {label}
              <span className="count">{count}</span>
            </button>
          ))}
        </div>
        <span className="muted" style={{ fontSize: 13 }}>
          {paged.range}
        </span>
      </div>
      {shown.length === 0 ? (
        <div className="empty">{filter === 'new' ? 'Nothing new in this file.' : 'None.'}</div>
      ) : (
        <div className="preview-table">
          <table className="bank-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th className="num">Amount</th>
                <th className="num">Balance</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {paged.rows.map((l) => (
                <tr key={l.row_index}>
                  <td className="date">{l.txn_date}</td>
                  <td>
                    <div className="faint mono clipped" title={l.narration}>
                      {l.narration}
                    </div>
                    {l.reference && <div className="faint">Ref {l.reference}</div>}
                  </td>
                  <td className={`num amt ${l.direction === 'debit' ? 'out' : 'in'}`}>
                    {l.direction === 'debit' ? '−' : '+'}
                    {formatInr(l.amount_paise)}
                  </td>
                  <td className="num muted">
                    {l.balance_after_paise === null ? '—' : formatInr(l.balance_after_paise)}
                  </td>
                  <td>
                    {l.is_new ? (
                      <span className="pill advisory">New</span>
                    ) : (
                      <span className="pill closed">
                        Already in
                        {l.posting_state && ALREADY_LABELS[l.posting_state]
                          ? ` · ${ALREADY_LABELS[l.posting_state]}`
                          : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />
    </section>
  )
}

//: A form that is not daily work, opened beside the page rather than open on it.
function Panel({
  title,
  onClose,
  wide = false,
  children,
}: {
  title: string
  onClose: () => void
  wide?: boolean
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside
        className={wide ? 'drawer wide' : 'drawer'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="drawer-head">
          <div>
            <h2>{title}</h2>
          </div>
          <button className="quiet close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="drawer-body panel-body">{children}</div>
      </aside>
    </>
  )
}

//: Sending one line to a Finance Head with a question attached. Small on
//: purpose: the Book Keeper is saying "I do not know what this is", and a form
//: that asked them to classify it first would be asking the question back.
function AskAbout({
  txn,
  onDone,
  onCancel,
}: {
  txn: Txn
  onDone: () => void
  onCancel: () => void
}) {
  const [note, setNote] = useState('')
  const [head, setHead] = useState('')
  const [clinic, setClinic] = useState('')

  const options = useQuery({
    queryKey: ['bank-question-options'],
    queryFn: () => api.get<AskOptions>('/banking/questions-options'),
  })
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/org/locations'),
  })
  const ask = useMutation({
    mutationFn: () =>
      api.post('/banking/questions', {
        bank_transaction_id: txn.id,
        note,
        finance_head_id: head,
        location_id: clinic || null,
      }),
    onSuccess: onDone,
  })

  return (
    <div className="inset">
      <strong>
        Ask about {formatInr(txn.amount_paise)} on {txn.txn_date}
      </strong>
      <ErrorNote error={ask.error} />
      <p className="faint mono" style={{ fontSize: 12, margin: '4px 0 8px' }}>
        {txn.narration_raw}
      </p>
      <Field label="What do you need to know?" caption="it travels with the line">
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <div className="row">
        <Field label="Send to" caption="finance decide who is asked next">
          <select value={head} onChange={(e) => setHead(e.target.value)}>
            <option value="">Choose…</option>
            {(options.data?.finance_heads ?? []).map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Clinic" caption="your best guess, if you have one">
          <select value={clinic} onChange={(e) => setClinic(e.target.value)}>
            <option value="">Not sure</option>
            {(locations.data ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="actions">
        <button
          className="primary"
          disabled={ask.isPending || !note.trim() || !head}
          onClick={() => ask.mutate()}
        >
          Ask
        </button>
        <button onClick={onCancel}>Cancel</button>
      </div>
      <p className="faint" style={{ margin: '8px 0 0' }}>
        The line cannot be posted while the question is open, and the question cannot be cancelled —
        if it sticks, a Finance Head can move it.
      </p>
    </div>
  )
}

export default function Banking() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)

  const [accountId, setAccountId] = useState('')
  const [importAccount, setImportAccount] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [pending, setPending] = useState<File | null>(null)
  const [onlyReview, setOnlyReview] = useState(false)
  const [asking, setAsking] = useState<Txn | null>(null)
  const [billing, setBilling] = useState<Txn | null>(null)
  const [paying, setPaying] = useState<Txn | null>(null)
  const [editing, setEditing] = useState<BankAccountRow | null>(null)
  const [panel, setPanel] = useState<PanelKind>(null)

  //: Raising a bank account, which the Admin and the Book Keeper both may.
  const canConfigure = can('org.bank_account.create')

  // Gated on the permission each *fetch* needs, not on the permission of one
  // of the things that reads it: a query that never runs leaves a dropdown
  // empty without any error to say why.
  const chart = useQuery({
    queryKey: ['coa-accounts'],
    queryFn: () => api.get<Account[]>('/coa/accounts'),
    enabled: can('org.coa.view'),
  })
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/org/locations'),
    enabled: can('org.location.view'),
  })
  //: Retired departments are left out of the choices but not out of the
  //: lookup, so a line tagged to one before it was retired still shows its name.
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<Department[]>('/org/departments'),
    enabled: can('org.department.view'),
  })
  const activeDepartments = (departments.data ?? []).filter((d) => d.is_active)
  const departmentName = (id: string | null): string =>
    id === null ? '—' : ((departments.data ?? []).find((d) => d.id === id)?.name ?? '—')

  const accounts = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<BankAccountRow[]>('/banking/accounts'),
  })
  const categories = useQuery({
    queryKey: ['bank-categories'],
    queryFn: () => api.get<BankCategory[]>('/banking/categories'),
  })
  const summary = useQuery({
    queryKey: ['bank-txns', 'summary', accountId],
    queryFn: () =>
      api.get<Summary>(
        `/banking/transactions/summary${accountId ? `?account_id=${accountId}` : ''}`,
      ),
  })
  //: 1-based, and back to 1 whenever the filters change: page 7 of the old
  //: list is no page at all of the new one.
  const [filters, setFilters] = useState<LineFilters>(NO_FILTERS)
  const [showMore, setShowMore] = useState(false)
  const setFilter = (patch: Partial<LineFilters>) => setFilters((f) => ({ ...f, ...patch }))
  //: Sent a moment after the last change, so typing a name is one request
  //: rather than one per letter.
  const wanted = filterQuery(filters)
  const [applied, setApplied] = useState(wanted)
  useEffect(() => {
    const timer = setTimeout(() => setApplied(wanted), 300)
    return () => clearTimeout(timer)
  }, [wanted])
  const filtering = applied !== ''
  const [page, setPage] = useState(1)
  useEffect(() => setPage(1), [accountId, onlyReview, applied])
  const txns = useQuery({
    queryKey: ['bank-txns', accountId, onlyReview, applied, page],
    queryFn: () =>
      api.get<TxnPage>(
        `/banking/transactions/page?limit=${PAGE}&offset=${(page - 1) * PAGE}${
          accountId ? `&account_id=${accountId}` : ''
        }${onlyReview ? '&needs_review=true' : ''}${applied}`,
      ),
    placeholderData: (previous) => previous,
  })
  const rows = txns.data?.rows ?? []
  const total = txns.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE))
  //: Posting the last line of the last page while "needs review" is on
  //: leaves the page empty; step back rather than show nothing.
  useEffect(() => {
    if (page > pages) setPage(pages)
  }, [page, pages])

  const upload = async (path: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    const response = await fetch(`/api/v1${path}`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    })
    const body = await response.json().catch(() => null)
    if (!response.ok) throw new Error(body?.detail ?? `upload failed (${response.status})`)
    return body
  }

  //: A bank account is agreed by somebody other than whoever added it: send
  //: it, agree to it, or send it back saying what is wrong. Until it is
  //: approved nothing can be loaded against it.
  const refreshAccounts = () => {
    void queryClient.invalidateQueries({ queryKey: ['bank-accounts'] })
  }
  const submit = useMutation({
    mutationFn: (id: string) => api.post(`/banking/accounts/${id}/submit`),
    onSuccess: refreshAccounts,
  })
  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/banking/accounts/${id}/approve`),
    onSuccess: refreshAccounts,
  })
  const sendBack = useMutation({
    mutationFn: (vars: { id: string; reason: string }) =>
      api.post(`/banking/accounts/${vars.id}/return`, { reason: vars.reason }),
    onSuccess: refreshAccounts,
  })

  const doPreview = useMutation({
    mutationFn: (vars: { file: File; accountId: string }) =>
      upload(
        `/banking/accounts/${vars.accountId}/statements/preview`,
        vars.file,
      ) as Promise<Preview>,
    onSuccess: (result) => setPreview(result),
  })
  const doImport = useMutation({
    mutationFn: (vars: { file: File; accountId: string }) =>
      upload(`/banking/accounts/${vars.accountId}/statements`, vars.file),
    onSuccess: () => {
      setPreview(null)
      setPending(null)
      if (fileRef.current) fileRef.current.value = ''
      void queryClient.invalidateQueries({ queryKey: ['bank-txns'] })
      setBulkResult('Statement imported.')
      setPanel(null)
    },
  })

  /**
   * An import needs an account and a file, in whichever order a person gives
   * them, so both come through here and whichever completes the pair starts
   * the read. A preview is counted against one account, so it is cleared on
   * every change rather than left above a button that would act on it.
   */
  const previewWhenReady = (file: File | null, account: string) => {
    setPending(file)
    setPreview(null)
    doPreview.reset()
    if (file && account) doPreview.mutate({ file, accountId: account })
  }

  //: Which rows are ticked. A Set: the only questions asked of it are "is this
  //: one in" and "how many".
  const [picked, setPicked] = useState<Set<string>>(new Set())
  //: A ticked line the new filter hides would still be posted or changed by
  //: the bulk bar, out of sight; a change of filter starts the ticks afresh.
  useEffect(() => setPicked(new Set()), [applied])
  const [bulkCategory, setBulkCategory] = useState('')
  const [bulkDepartment, setBulkDepartment] = useState('')
  const [bulkResult, setBulkResult] = useState<string | null>(null)

  const togglePick = (id: string) =>
    setPicked((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  /** What a bulk call answers with: how many took, and which did not and why. */
  const describe = (verb: string, out: { succeeded: number; failed: { reason: string }[] }) => {
    if (out.failed.length === 0) return `${out.succeeded} ${verb}.`
    const reasons = [...new Set(out.failed.map((f) => f.reason))]
    return `${out.succeeded} ${verb}, ${out.failed.length} refused. ` + reasons.join(' ')
  }

  const runRules = useMutation({
    mutationFn: () =>
      api.post<{
        considered: number
        categorised: number
        suggested: number
        untouched: number
      }>('/banking/rules/apply', { account_id: accountId || null }),
    onSuccess: (out) => {
      setBulkResult(
        out.considered === 0
          ? 'Nothing was waiting for a category.'
          : `${out.categorised} categorised, ${out.suggested} suggested for you to agree with, ` +
              `${out.untouched} the rules did not recognise.`,
      )
      void queryClient.invalidateQueries({ queryKey: ['bank-txns'] })
    },
  })
  const categorizeMany = useMutation({
    mutationFn: () =>
      api.post<{ succeeded: number; failed: { reason: string }[] }>(
        '/banking/transactions/categorize',
        { transaction_ids: [...picked], category_id: bulkCategory || null },
      ),
    onSuccess: (out) => {
      setBulkResult(describe('categorised', out))
      setPicked(new Set())
      void queryClient.invalidateQueries({ queryKey: ['bank-txns'] })
    },
  })
  //: Many lines to one department. Sends the department and nothing else: an
  //: empty category here would clear what each line already said it was for.
  const departmentMany = useMutation({
    mutationFn: () =>
      api.post<{ succeeded: number; failed: { reason: string }[] }>(
        '/banking/transactions/categorize',
        { transaction_ids: [...picked], department_id: bulkDepartment },
      ),
    onSuccess: (out) => {
      setBulkResult(describe('given a department', out))
      setPicked(new Set())
      void queryClient.invalidateQueries({ queryKey: ['bank-txns'] })
    },
  })
  const postMany = useMutation({
    mutationFn: () =>
      api.post<{ succeeded: number; failed: { reason: string }[] }>('/banking/transactions/post', {
        transaction_ids: [...picked],
      }),
    onSuccess: (out) => {
      setBulkResult(describe('posted', out))
      setPicked(new Set())
      void queryClient.invalidateQueries({ queryKey: ['bank-txns'] })
      void queryClient.invalidateQueries({ queryKey: ['bank-accounts'] })
    },
  })
  const categorize = useMutation({
    mutationFn: (vars: { id: string; categoryId: string }) =>
      api.post<Txn>(`/banking/transactions/${vars.id}/categorize`, {
        category_id: vars.categoryId || null,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['bank-txns'] }),
  })
  //: Whose spending a line was, sent on its own so the category stays as it
  //: stood. Not offered once a line is in the books: a posted line is
  //: corrected by reversing it rather than by relabelling it here.
  const assignDepartment = useMutation({
    mutationFn: (vars: { id: string; departmentId: string }) =>
      api.post<Txn>(`/banking/transactions/${vars.id}/categorize`, {
        department_id: vars.departmentId || null,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['bank-txns'] }),
  })
  //: Put a payment question's answer on its line, as the answer said it.
  const applyAnswer = useMutation({
    mutationFn: (a: NonNullable<Txn['answer']>) =>
      api.post(`/banking/questions/${a.question_id}/apply`, {
        location_id: a.location_id,
        category_id: a.category_id,
        department_id: a.department_id,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bank-txns'] })
      void queryClient.invalidateQueries({ queryKey: ['bank-questions'] })
    },
  })
  //: Whose money a line was, sent on its own like the department.
  const nameParty = useMutation({
    mutationFn: (vars: { id: string; partyId: string }) =>
      api.post<Txn>(`/banking/transactions/${vars.id}/categorize`, {
        party_id: vars.partyId || null,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['bank-txns'] }),
  })
  //: Who may be named: approved parties -- and on a line that posts to 2530,
  //: approved director ledgers only (Books -> Director ledgers).
  const parties = useQuery({
    queryKey: ['parties'],
    queryFn: () => api.get<{ id: string; name: string; status: string }[]>('/parties'),
    enabled: can('party.vendor.view'),
  })
  const directorLedgers = useQuery({
    queryKey: ['parties', 'director'],
    queryFn: () =>
      api.get<{ id: string; code: string; name: string; status: string }[]>(
        '/parties?kind=director',
      ),
    enabled: can('party.vendor.view'),
  })
  //: On a line that posts to 1530 or 4950: open investment ledgers only.
  const investmentLedgers = useQuery({
    queryKey: ['parties', 'investment'],
    queryFn: () =>
      api.get<{ id: string; code: string; name: string; status: string }[]>(
        '/parties?kind=investment',
      ),
    enabled: can('party.vendor.view'),
  })
  const partyChoices = (parties.data ?? [])
    .filter((p) => p.status === 'approved')
    .map((p) => ({ value: p.id, label: p.name }))
  const ledgerChoices = (rows?: { id: string; code: string; name: string; status: string }[]) =>
    (rows ?? [])
      .filter((p) => p.status === 'approved')
      .map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))
  const directorChoices = ledgerChoices(directorLedgers.data)
  const investmentChoices = ledgerChoices(investmentLedgers.data)
  const choicesFor = (t: Txn) =>
    t.party_kind_expected === 'director'
      ? directorChoices
      : t.party_kind_expected === 'investment'
        ? investmentChoices
        : partyChoices
  const post = useMutation({
    mutationFn: (id: string) => api.post<Txn>(`/banking/transactions/${id}/post`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bank-txns'] })
      void queryClient.invalidateQueries({ queryKey: ['tb'] })
    },
  })

  if (accounts.isLoading) return <Loading what="Loading bank accounts" />
  const accountRows = accounts.data ?? []
  const viewed = accountRows.find((a) => a.id === accountId) ?? null
  const s = summary.data
  //: Rows the bulk actions can act on. A line in the books is finished, and
  //: half of a transfer is already accounted for by the entry its pair produced.
  const selectable = rows.filter(
    (t) => !inTheBooks(t) && t.transfer_pair_id === null && t.open_question_id === null,
  )
  const openImport = () => {
    setImportAccount(accountId || (accountRows.find((a) => a.status === 'approved')?.id ?? ''))
    setPreview(null)
    setPending(null)
    doPreview.reset()
    setPanel('import')
  }

  return (
    <>
      <ErrorNote error={accounts.error ?? submit.error ?? approve.error ?? sendBack.error} />

      {accountRows.length === 0 && (
        <Note kind="warn">
          No bank accounts yet, so there is nowhere to import a statement to. Each one needs its own
          ledger account — there is no shared “Bank” account, because a payment has to name the bank
          it left from.
          {!canConfigure && ' The Book Keeper or an administrator adds one.'}
        </Note>
      )}

      <div className="bank-toolbar">
        {can('banking.statement.create') && accountRows.length > 0 && (
          <button className="primary" onClick={openImport}>
            Import statement
          </button>
        )}
        {accountRows.length > 0 && can('banking.transaction.categorize') && (
          <button
            disabled={runRules.isPending}
            onClick={() => runRules.mutate()}
            title="Rules only ever set a category. They never post, and never replace one somebody chose."
          >
            {runRules.isPending ? 'Running…' : 'Run the rules'}
          </button>
        )}
        {accountRows.length > 0 && can('banking.transaction.categorize') && (
          <button onClick={() => setPanel('categories')}>Categories</button>
        )}
        {accountRows.length > 0 && can('banking.transaction.post') && (
          <button onClick={() => setPanel('opening')}>Opening balance</button>
        )}
        {canConfigure && <button onClick={() => setPanel('add-account')}>Add bank account</button>}
        {can('banking.rule.view') && (
          <Link className="bank-toolbar-link" to="/books/rules">
            Categorisation rules →
          </Link>
        )}
      </div>

      {accountRows.length > 0 && (
        <div className="acct-cards">
          <button
            className={`acct-card${accountId === '' ? ' on' : ''}`}
            onClick={() => setAccountId('')}
          >
            <div className="acct-name">All accounts</div>
            <div className="faint">
              {accountRows.length} account{accountRows.length === 1 ? '' : 's'}
            </div>
          </button>
          {accountRows.map((a) => (
            <div
              key={a.id}
              className={`acct-card${accountId === a.id ? ' on' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setAccountId(a.id)}
              onKeyDown={(e) => e.key === 'Enter' && setAccountId(a.id)}
            >
              <div className="acct-top">
                <div className="acct-name">{a.name}</div>
                <span className={`pill ${STATUS_TONE[a.status] ?? 'closed'}`}>{a.status}</span>
              </div>
              <div className="muted">
                {a.bank_name} · <span className="mono">{a.account_number_masked}</span>
              </div>
              <div className="faint">
                {a.kind} · GL {a.gl_account_code ?? '—'}
                {KINDS_AT_A_BRANCH.has(a.kind) && (
                  <> · IFSC {a.ifsc ? <span className="mono">{a.ifsc}</span> : <em>missing</em>}</>
                )}
              </div>
              {(a.status !== 'approved' || canConfigure) && (
                <div className="acct-actions" onClick={(e) => e.stopPropagation()}>
                  {(a.status === 'draft' || a.status === 'returned') && canConfigure && (
                    <button className="small" onClick={() => setEditing(a)}>
                      Edit
                    </button>
                  )}
                  {(a.status === 'draft' || a.status === 'returned') &&
                    can('org.bank_account.submit') && (
                      <button
                        className="small"
                        // Not sent for approval without the branch it sits at;
                        // the server refuses it too.
                        disabled={submit.isPending || (KINDS_AT_A_BRANCH.has(a.kind) && !a.ifsc)}
                        title={
                          KINDS_AT_A_BRANCH.has(a.kind) && !a.ifsc
                            ? 'Add the IFSC first — use Edit.'
                            : undefined
                        }
                        onClick={() => submit.mutate(a.id)}
                      >
                        Send for approval
                      </button>
                    )}
                  {a.status === 'submitted' && can('org.bank_account.approve') && (
                    <>
                      <button
                        className="small primary"
                        disabled={approve.isPending}
                        onClick={() => approve.mutate(a.id)}
                      >
                        Approve
                      </button>
                      <button
                        className="small"
                        disabled={sendBack.isPending}
                        onClick={() => {
                          const reason = window.prompt(
                            'What needs correcting? The Book Keeper sees this.',
                          )
                          if (reason && reason.trim()) {
                            sendBack.mutate({
                              id: a.id,
                              reason: reason.trim(),
                            })
                          }
                        }}
                      >
                        Send back
                      </button>
                    </>
                  )}
                  {a.status === 'submitted' && !can('org.bank_account.approve') && (
                    <span className="faint">with a Finance Head</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        // Keyed by the account, so opening a second one starts from that
        // account's details rather than from whatever was typed into the first.
        <EditBankAccount key={editing.id} account={editing} onClose={() => setEditing(null)} />
      )}

      {s && (
        <div className="grid">
          <Stat
            label="To post"
            value={String(s.to_post)}
            note={
              s.to_post
                ? `in ${formatInr(s.to_post_in_paise)} · out ${formatInr(s.to_post_out_paise)}`
                : 'everything is in the books'
            }
          />
          <Stat
            label="Missing category / department"
            value={`${s.missing_category} / ${s.missing_department}`}
            note="a line posts only once it says both"
          />
          <Stat
            label="Held"
            value={String(s.held)}
            note="a question open, or waiting on an approver"
          />
          <Stat
            label={viewed ? `${viewed.name} statement` : 'Statements cover'}
            value={s.last_date ?? '—'}
            note={
              s.first_date
                ? `from ${s.first_date} · ${s.in_books} of ${s.total} in the books`
                : 'no lines yet'
            }
          />
        </div>
      )}

      {accountRows.length > 0 && <ClosingBalances accountId={accountId} />}

      <section className="card wide">
        <header className="appr-toolbar">
          <div className="seg" role="tablist">
            <button
              role="tab"
              aria-selected={!onlyReview}
              className={!onlyReview ? 'on' : undefined}
              onClick={() => setOnlyReview(false)}
            >
              All lines
              <span className="count">{onlyReview ? '' : total}</span>
            </button>
            <button
              role="tab"
              aria-selected={onlyReview}
              className={onlyReview ? 'on' : undefined}
              onClick={() => setOnlyReview(true)}
            >
              Needs review
              {onlyReview && <span className="count">{total}</span>}
            </button>
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            {viewed ? viewed.name : 'All accounts'}
            {filtering && ` · ${total} ${total === 1 ? 'line matches' : 'lines match'}`}
            {total > PAGE &&
              ` · ${(page - 1) * PAGE + 1}–${Math.min(page * PAGE, total)} of ${total}`}
          </div>
        </header>
        <div className="bank-filters">
          <div className="appr-filters">
            <input
              type="search"
              placeholder="Party, narration, reference or amount"
              aria-label="Search bank lines"
              value={filters.q}
              onChange={(e) => setFilter({ q: e.target.value })}
            />
            <select
              aria-label="Money in or out"
              value={filters.direction}
              onChange={(e) => setFilter({ direction: e.target.value as LineFilters['direction'] })}
            >
              <option value="">In and out</option>
              <option value="credit">Money in (credit)</option>
              <option value="debit">Money out (debit)</option>
            </select>
            <select
              aria-label="Channel"
              value={filters.channel}
              onChange={(e) => setFilter({ channel: e.target.value })}
            >
              <option value="">Every channel</option>
              {CHANNELS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <SearchSelect
              aria-label="Category"
              value={filters.category}
              onChange={(v) => setFilter({ category: v })}
              empty="Every category"
              options={[
                { value: 'none', label: 'Not categorised' },
                ...(categories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
            <select
              aria-label="Posted or not"
              value={filters.inBooks}
              onChange={(e) => setFilter({ inBooks: e.target.value as LineFilters['inBooks'] })}
            >
              <option value="">Posted or not</option>
              <option value="no">Not posted yet</option>
              <option value="yes">In the books</option>
            </select>
            <button
              type="button"
              className={`small${showMore ? ' on' : ''}`}
              aria-expanded={showMore}
              onClick={() => setShowMore(!showMore)}
            >
              Dates &amp; amounts
              {moreSet(filters) > 0 && <span className="count">{moreSet(filters)}</span>}
            </button>
            {wanted !== '' && (
              <button type="button" className="quiet small" onClick={() => setFilters(NO_FILTERS)}>
                Clear filters
              </button>
            )}
          </div>
          {showMore && (
            <div className="appr-filters bank-filters-more">
              <label>
                From
                <input
                  type="date"
                  value={filters.from}
                  max={filters.to || undefined}
                  onChange={(e) => setFilter({ from: e.target.value })}
                />
              </label>
              <label>
                To
                <input
                  type="date"
                  value={filters.to}
                  min={filters.from || undefined}
                  onChange={(e) => setFilter({ to: e.target.value })}
                />
              </label>
              <label>
                Amount from ₹
                <input
                  className="num"
                  inputMode="decimal"
                  placeholder="0"
                  value={filters.min}
                  onChange={(e) => setFilter({ min: e.target.value })}
                />
              </label>
              <label>
                to ₹
                <input
                  className="num"
                  inputMode="decimal"
                  placeholder="any"
                  value={filters.max}
                  onChange={(e) => setFilter({ max: e.target.value })}
                />
              </label>
            </div>
          )}
        </div>
        <div className="body tight">
          <ErrorNote
            error={
              txns.error ??
              post.error ??
              categorize.error ??
              nameParty.error ??
              applyAnswer.error ??
              assignDepartment.error ??
              categorizeMany.error ??
              departmentMany.error ??
              postMany.error ??
              runRules.error
            }
          />
          {bulkResult && (
            <div className="bank-result">
              <Note kind="info">{bulkResult}</Note>
              <button className="quiet small" onClick={() => setBulkResult(null)}>
                Dismiss
              </button>
            </div>
          )}
          {picked.size > 0 && (
            <div className="bulk-bar">
              <strong>{picked.size} selected</strong>
              {can('banking.transaction.categorize') && (
                <>
                  <SearchSelect
                    className="bulk-category"
                    aria-label="Category for the selected lines"
                    value={bulkCategory}
                    onChange={setBulkCategory}
                    empty="Uncategorised"
                    options={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
                  />
                  <button
                    disabled={categorizeMany.isPending}
                    onClick={() => categorizeMany.mutate()}
                  >
                    {categorizeMany.isPending ? 'Applying…' : 'Set category'}
                  </button>
                  <select
                    value={bulkDepartment}
                    onChange={(e) => setBulkDepartment(e.target.value)}
                  >
                    <option value="">Department…</option>
                    {activeDepartments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  <button
                    disabled={departmentMany.isPending || !bulkDepartment}
                    title={
                      bulkDepartment
                        ? undefined
                        : 'Choose a department first — this never clears one.'
                    }
                    onClick={() => departmentMany.mutate()}
                  >
                    {departmentMany.isPending ? 'Applying…' : 'Set department'}
                  </button>
                </>
              )}
              {can('banking.transaction.post') && (
                <button
                  className="primary"
                  disabled={postMany.isPending}
                  onClick={() => postMany.mutate()}
                >
                  {postMany.isPending ? 'Posting…' : `Post ${picked.size}`}
                </button>
              )}
              <button className="quiet" onClick={() => setPicked(new Set())}>
                Clear
              </button>
            </div>
          )}
          {rows.length === 0 ? (
            <div className="empty">
              {filtering ? (
                <>
                  No line matches these filters.{' '}
                  <button
                    type="button"
                    className="quiet small"
                    onClick={() => setFilters(NO_FILTERS)}
                  >
                    Clear filters
                  </button>
                </>
              ) : onlyReview ? (
                'Nothing needs review.'
              ) : (
                'No lines yet — import a statement.'
              )}
            </div>
          ) : (
            <table className="bank-table">
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    {/* Ticks everything the bulk actions could act on, which is
                        not everything shown. */}
                    <input
                      type="checkbox"
                      aria-label="Select all on this page"
                      checked={selectable.length > 0 && picked.size === selectable.length}
                      onChange={(e) =>
                        setPicked(new Set(e.target.checked ? selectable.map((t) => t.id) : []))
                      }
                    />
                  </th>
                  <th>Date</th>
                  <th>Description</th>
                  <th className="num">Amount</th>
                  <th>Category</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const stand = standing(t)
                  //: Not once somebody has accepted it: the acceptance is the approval.
                  const control = t.unplanned_accepted_by
                    ? undefined
                    : CONTROL_LABELS[t.control_state]
                  return (
                    <Fragment key={t.id}>
                      <tr className={picked.has(t.id) ? 'picked' : undefined}>
                        <td>
                          {!inTheBooks(t) &&
                            t.transfer_pair_id === null &&
                            t.open_question_id === null && (
                              <input
                                type="checkbox"
                                aria-label="Select line"
                                checked={picked.has(t.id)}
                                onChange={() => togglePick(t.id)}
                              />
                            )}
                        </td>
                        <td className="date">
                          {t.txn_date}
                          <div className="faint">{t.channel}</div>
                        </td>
                        <td>
                          <div className="bank-who">
                            {t.counterparty ?? (t.direction === 'debit' ? 'Payment' : 'Receipt')}
                          </div>
                          <div className="faint mono clipped" title={t.narration_raw}>
                            {t.narration_raw}
                          </div>
                        </td>
                        <td className={`num amt ${t.direction === 'debit' ? 'out' : 'in'}`}>
                          {t.direction === 'debit' ? '−' : '+'}
                          {formatInr(t.amount_paise)}
                          {t.balance_after_paise !== null && (
                            <div className="faint" title="The bank’s balance after this line">
                              bal {signedInr(t.balance_after_paise)}
                            </div>
                          )}
                        </td>
                        <td className="cat">
                          {inTheBooks(t) || t.category_locked ? (
                            // Locked when the money left without an approved
                            // request: what it was is the approver's to say.
                            <span className="muted">
                              {categories.data?.find((c) => c.id === t.category_id)?.name ?? '—'}
                              {t.category_locked && !inTheBooks(t) && (
                                <div className="faint">
                                  {t.unplanned_accepted_by
                                    ? `chosen by ${t.unplanned_accepted_by}`
                                    : 'for the approver to say'}
                                </div>
                              )}
                            </span>
                          ) : can('banking.transaction.categorize') ? (
                            <SearchSelect
                              className={t.category_id ? undefined : 'unset'}
                              aria-label="Category"
                              value={t.category_id ?? ''}
                              onChange={(v) =>
                                categorize.mutate({
                                  id: t.id,
                                  categoryId: v,
                                })
                              }
                              empty="Uncategorised"
                              options={(categories.data ?? []).map((c) => ({
                                value: c.id,
                                label: c.name,
                              }))}
                            />
                          ) : (
                            <span className="faint">—</span>
                          )}
                          {t.answer &&
                            !inTheBooks(t) &&
                            !(t.answer.on_line && t.category_locked) && (
                              <div className={t.answer.on_line ? 'faint' : 'answer-waiting'}>
                                {t.answer.on_line ? (
                                  `answered by ${t.answer.answered_by ?? '—'}`
                                ) : (
                                  <>
                                    <span title={t.answer.text ?? undefined}>
                                      Answered by {t.answer.answered_by ?? '—'}
                                      {t.answer.text ? `: “${t.answer.text}”` : ''}
                                    </span>
                                    {can('banking.question.create') && (
                                      <button
                                        type="button"
                                        className="small"
                                        disabled={applyAnswer.isPending}
                                        onClick={() => t.answer && applyAnswer.mutate(t.answer)}
                                      >
                                        Use the answer
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          {inTheBooks(t)
                            ? t.party_name && <div className="faint">{t.party_name}</div>
                            : (t.party_required || t.party_id) &&
                              can('banking.transaction.categorize') &&
                              (t.party_kind_expected &&
                              SUBLEDGER[t.party_kind_expected] &&
                              choicesFor(t).length === 0 ? (
                                <div className="answer-waiting">
                                  <span>{SUBLEDGER[t.party_kind_expected].none}</span>
                                  {can('party.ledger.view') && (
                                    <Link to={SUBLEDGER[t.party_kind_expected].path}>
                                      {SUBLEDGER[t.party_kind_expected].screen}
                                    </Link>
                                  )}
                                </div>
                              ) : (
                                <SearchSelect
                                  className={`line-party${t.party_id ? '' : ' unset'}`}
                                  aria-label={
                                    SUBLEDGER[t.party_kind_expected ?? '']?.label ?? 'Whose money'
                                  }
                                  value={t.party_id ?? ''}
                                  onChange={(v) => nameParty.mutate({ id: t.id, partyId: v })}
                                  placeholder={`${
                                    SUBLEDGER[t.party_kind_expected ?? '']?.label ?? 'Whose money'
                                  }? Choose…`}
                                  options={choicesFor(t)}
                                />
                              ))}
                        </td>
                        {/* A dropdown only until the line is in the books: the
                            journal carries the department it was posted with,
                            and a posted line is corrected by reversing it. */}
                        <td style={{ minWidth: 150 }}>
                          {!inTheBooks(t) &&
                          can('banking.transaction.categorize') &&
                          can('org.department.view') ? (
                            <select
                              className={t.department_id ? undefined : 'unset'}
                              value={t.department_id ?? ''}
                              style={{ minWidth: 134 }}
                              disabled={assignDepartment.isPending}
                              onChange={(e) =>
                                assignDepartment.mutate({
                                  id: t.id,
                                  departmentId: e.target.value,
                                })
                              }
                            >
                              <option value="">Unassigned</option>
                              {activeDepartments.map((d) => (
                                <option key={d.id} value={d.id}>
                                  {d.name}
                                </option>
                              ))}
                              {t.department_id !== null &&
                                !activeDepartments.some((d) => d.id === t.department_id) && (
                                  <option value={t.department_id}>
                                    {departmentName(t.department_id)} (retired)
                                  </option>
                                )}
                            </select>
                          ) : (
                            <span className={t.department_id ? 'muted' : 'faint'}>
                              {departmentName(t.department_id)}
                            </span>
                          )}
                        </td>
                        <td style={{ minWidth: 180 }}>
                          <span className={`pill ${stand.tone}`}>{stand.label}</span>
                          {control && (
                            <>
                              {' '}
                              <span
                                className={`pill ${t.control_state}`}
                                title="Flagged by the bank debit control — with the Finance Head on the reconciliation screen"
                              >
                                {control}
                              </span>
                            </>
                          )}
                          {stand.detail && (
                            <div className="faint bank-detail" title={stand.detail}>
                              {stand.detail}
                            </div>
                          )}
                          {t.gst_bill && (
                            <div>
                              <span
                                className={`pill ${gstBillNote(t.gst_bill).tone}`}
                                title="The supplier’s GST invoice behind this payment"
                              >
                                {gstBillNote(t.gst_bill).text}
                              </span>
                            </div>
                          )}
                          {t.vendor_payment && (
                            <div>
                              <span
                                className={`pill ${vendorPaymentNote(t.vendor_payment).tone}`}
                                title="The vendor bills this payment was set against"
                              >
                                {vendorPaymentNote(t.vendor_payment).text}
                              </span>
                            </div>
                          )}
                          {t.open_bills && (
                            <div
                              className="faint bank-detail"
                              title="Pay the bills from this line, rather than post it to an expense the bill already booked"
                            >
                              {t.open_bills.party_name} has{' '}
                              {formatInr(t.open_bills.outstanding_paise)} open on{' '}
                              {t.open_bills.count} bill
                              {t.open_bills.count > 1 ? 's' : ''} — pay{' '}
                              {t.open_bills.count > 1 ? 'them' : 'it'} from this?
                            </div>
                          )}
                          {t.invoices_mapped > 0 && (
                            <div>
                              <a href={`/api/v1/banking/transactions/${t.id}/invoices.pdf`}>
                                Invoices ({t.invoices_mapped}) PDF
                              </a>
                            </div>
                          )}
                        </td>
                        <td className="num" style={{ whiteSpace: 'nowrap' }}>
                          {!inTheBooks(t) &&
                            t.transfer_pair_id === null &&
                            t.open_question_id === null &&
                            t.posting_blocked === null &&
                            t.gst_bill === null &&
                            t.vendor_payment === null &&
                            can('banking.transaction.post') && (
                              <button
                                className="small primary"
                                disabled={post.isPending || !classified(t)}
                                title={
                                  classified(t)
                                    ? undefined
                                    : `Set ${missingOn(t)} first — a line that does not say ` +
                                      'what it was for or which department used it is not posted.'
                                }
                                onClick={() => post.mutate(t.id)}
                              >
                                Post
                              </button>
                            )}{' '}
                          {!inTheBooks(t) &&
                            t.transfer_pair_id === null &&
                            t.open_question_id === null &&
                            can('banking.question.create') && (
                              <button
                                className="small"
                                onClick={() => setAsking(asking?.id === t.id ? null : t)}
                              >
                                Ask
                              </button>
                            )}
                          {(t.gst_bill !== null || mayCarryAGstBill(t)) &&
                            classified(t) &&
                            can('ap.invoice.create') &&
                            can('banking.transaction.post') && (
                              <>
                                {' '}
                                <button
                                  className="small"
                                  title={
                                    t.gst_bill
                                      ? 'The bill behind this payment, and its invoice'
                                      : 'This payment has a GST invoice: record it, and the GST becomes credit to claim'
                                  }
                                  onClick={() => setBilling(billing?.id === t.id ? null : t)}
                                >
                                  {t.gst_bill ? 'Invoice' : 'GST invoice'}
                                </button>
                              </>
                            )}
                          {mayPayBills(t) &&
                            can('ap.payment.prepare') &&
                            can('banking.transaction.post') && (
                              <>
                                {' '}
                                <button
                                  className={`small${t.open_bills && !t.vendor_payment ? ' primary' : ''}`}
                                  title={
                                    t.vendor_payment
                                      ? 'The bills this payment paid'
                                      : inTheBooks(t)
                                        ? 'Posted on its own: move it onto the vendor’s bills (the Finance Head approves)'
                                        : 'Set this payment against the vendor’s bills, part or whole'
                                  }
                                  onClick={() => setPaying(paying?.id === t.id ? null : t)}
                                >
                                  {t.vendor_payment
                                    ? 'Bills paid'
                                    : inTheBooks(t)
                                      ? 'Move to bills'
                                      : 'Pay bills'}
                                </button>
                              </>
                            )}
                          {t.open_question_id !== null && (
                            <Link className="small" to="/books/questions">
                              See the question
                            </Link>
                          )}
                        </td>
                      </tr>
                      {asking?.id === t.id && (
                        // Opens under the line it is about, not at the top of
                        // the card, so it is plain which line is being asked.
                        <tr>
                          <td colSpan={8} style={{ padding: '4px 12px 12px' }}>
                            <AskAbout
                              txn={t}
                              onDone={() => {
                                setAsking(null)
                                void queryClient.invalidateQueries({
                                  queryKey: ['bank-txns'],
                                })
                              }}
                              onCancel={() => setAsking(null)}
                            />
                          </td>
                        </tr>
                      )}
                      {paying?.id === t.id && (
                        <tr>
                          <td colSpan={8} style={{ padding: '4px 12px 12px' }}>
                            <PayBills
                              txnId={t.id}
                              onDone={() =>
                                void queryClient.invalidateQueries({
                                  queryKey: ['bank-txns'],
                                })
                              }
                              onCancel={() => setPaying(null)}
                            />
                          </td>
                        </tr>
                      )}
                      {billing?.id === t.id && (
                        <tr>
                          <td colSpan={8} style={{ padding: '4px 12px 12px' }}>
                            <GstBillPanel
                              txn={t}
                              onDone={() =>
                                void queryClient.invalidateQueries({
                                  queryKey: ['bank-txns'],
                                })
                              }
                              onCancel={() => setBilling(null)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          )}
          {pages > 1 && (
            <div className="pager">
              <button
                type="button"
                className="small"
                disabled={page <= 1 || txns.isFetching}
                onClick={() => setPage(1)}
              >
                « First
              </button>
              <button
                type="button"
                className="small"
                disabled={page <= 1 || txns.isFetching}
                onClick={() => setPage(page - 1)}
              >
                ‹ Previous
              </button>
              <span className="muted">
                Page{' '}
                <input
                  className="num"
                  aria-label="Page"
                  style={{ width: 56, display: 'inline-block' }}
                  value={page}
                  onChange={(e) => {
                    const wanted = Number(e.target.value)
                    if (Number.isInteger(wanted) && wanted >= 1 && wanted <= pages) setPage(wanted)
                  }}
                />{' '}
                of {pages}
                {txns.isFetching && <span className="faint"> · loading…</span>}
              </span>
              <button
                type="button"
                className="small"
                disabled={page >= pages || txns.isFetching}
                onClick={() => setPage(page + 1)}
              >
                Next ›
              </button>
              <button
                type="button"
                className="small"
                disabled={page >= pages || txns.isFetching}
                onClick={() => setPage(pages)}
              >
                Last »
              </button>
            </div>
          )}
        </div>
      </section>

      <details className="appr-how">
        <summary>How a bank line reaches the books</summary>
        <p className="muted">
          Categorising is not posting: a category says what a line is <em>for</em>, and the ledger
          only moves when somebody posts it. A line is posted only once it says two things — what it
          was for, and which department used it. Until it does it stays out of the books, so the
          bank balance there is short by exactly that amount; that gap is deliberate, and it is what
          stops an unexplained payment being quietly filed away. Two lines that are one transfer
          between our own accounts post as a single entry. The department goes onto the ledger entry
          with the line, so it can be changed freely until the line is posted and not after: a
          posted line is corrected by reversing it. To give many lines the same department, tick
          them and use <em>Set department</em>.
        </p>
      </details>

      {panel === 'import' && (
        <Panel title="Import a statement" wide={preview !== null} onClose={() => setPanel(null)}>
          <p className="muted" style={{ marginTop: 0 }}>
            Previewed first — nothing is written until you agree. Lines already imported are
            recognised and skipped.
          </p>
          <ErrorNote error={doPreview.error ?? doImport.error} />
          <Field label="Account">
            <select
              value={importAccount}
              onChange={(e) => {
                setImportAccount(e.target.value)
                previewWhenReady(pending, e.target.value)
              }}
            >
              <option value="">Choose an account…</option>
              {accountRows.map((a) => (
                <option key={a.id} value={a.id} disabled={a.status !== 'approved'}>
                  {a.name}
                  {a.status !== 'approved' ? ` (${a.status})` : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Statement file" caption="CSV, text or Excel, as the bank gives it">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.txt,.xls,.xlsx"
              onChange={(e) => previewWhenReady(e.target.files?.[0] ?? null, importAccount)}
            />
          </Field>
          {pending && !importAccount && (
            <Note kind="info">
              {pending.name} is ready. Choose the account it belongs to and it will be read.
            </Note>
          )}
          {doPreview.isPending && <Loading what="Reading the statement" />}
          {preview && (
            <>
              {preview.refusals.length > 0 ? (
                <Note kind="error">{preview.refusals.join(' ')}</Note>
              ) : (
                <dl className="appr-facts">
                  <div>
                    <dt>Bank</dt>
                    <dd>{preview.bank ?? 'Unknown'}</dd>
                  </div>
                  <div>
                    <dt>Period</dt>
                    <dd>
                      {preview.period_start ?? '?'} → {preview.period_end ?? '?'}
                    </dd>
                  </div>
                  <div>
                    <dt>Lines</dt>
                    <dd>
                      <strong>{preview.new_count} new</strong> · {preview.duplicate_count} already
                      imported
                    </dd>
                  </div>
                  {preview.opening_balance_paise !== null && (
                    <div>
                      <dt>Balance</dt>
                      <dd>
                        {formatInr(preview.opening_balance_paise)} →{' '}
                        {formatInr(preview.closing_balance_paise ?? 0)}
                      </dd>
                    </div>
                  )}
                </dl>
              )}
              {preview.warnings.map((w, i) => (
                <Note key={i} kind="warn">
                  {w}
                </Note>
              ))}
              <PreviewLines
                key={preview.filename + preview.row_count}
                lines={preview.lines ?? []}
              />
              {preview.importable && (
                <div className="actions">
                  <button
                    className="primary"
                    disabled={doImport.isPending || !pending || !importAccount}
                    onClick={() =>
                      pending &&
                      importAccount &&
                      doImport.mutate({
                        file: pending,
                        accountId: importAccount,
                      })
                    }
                  >
                    {doImport.isPending ? 'Importing…' : `Import ${preview.new_count} lines`}
                  </button>
                </div>
              )}
            </>
          )}
        </Panel>
      )}

      {panel === 'categories' && (
        <Panel title="Categories" onClose={() => setPanel(null)}>
          <AddCategory categories={categories.data ?? []} chart={chart.data ?? []} />
        </Panel>
      )}

      {panel === 'opening' && (
        <Panel title="Opening balance" onClose={() => setPanel(null)}>
          <OpeningBalance accounts={accountRows} />
        </Panel>
      )}

      {panel === 'add-account' && (
        <Panel title="Add a bank account" onClose={() => setPanel(null)}>
          <AddBankAccount
            accounts={accountRows}
            chart={chart.data ?? []}
            locations={locations.data ?? []}
          />
        </Panel>
      )}
    </>
  )
}
